import React from 'react';
import { Text as RNText, type TextProps, type TextStyle } from 'react-native';
import { font, useTheme, type Colors } from './theme';

type Variant = 'body' | 'muted' | 'small' | 'caption' | 'label' | 'title' | 'h1' | 'h2' | 'h3' | 'value' | 'mono';

const VARIANTS: Record<Variant, (c: Colors) => TextStyle> = {
  body: (c) => ({ fontSize: font.size.base, color: c.text, lineHeight: 20 }),
  muted: (c) => ({ fontSize: font.size.md, color: c.muted, lineHeight: 18 }),
  small: (c) => ({ fontSize: font.size.sm, color: c.muted, lineHeight: 16 }),
  caption: (c) => ({ fontSize: font.size.xs, color: c.faint, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: font.weight.semibold }),
  label: (c) => ({ fontSize: font.size.md, color: c.text, fontWeight: font.weight.medium }),
  title: (c) => ({ fontSize: font.size.lg, color: c.text, fontWeight: font.weight.semibold }),
  h1: (c) => ({ fontSize: font.size.xxl, color: c.text, fontWeight: font.weight.bold, letterSpacing: -0.4 }),
  h2: (c) => ({ fontSize: font.size.xl, color: c.text, fontWeight: font.weight.semibold, letterSpacing: -0.2 }),
  h3: (c) => ({ fontSize: font.size.lg, color: c.text, fontWeight: font.weight.semibold }),
  value: (c) => ({ fontSize: 22, color: c.text, fontWeight: font.weight.semibold, fontVariant: ['tabular-nums'] }),
  mono: (c) => ({ fontSize: font.size.md, color: c.text, fontFamily: 'monospace' }),
};

export interface AppTextProps extends TextProps {
  variant?: Variant;
  color?: string;
  weight?: keyof typeof font.weight;
  align?: TextStyle['textAlign'];
  size?: number;
}

/** Themed text. `<Text variant="muted">`, `<Text variant="h2">`, `<Text color={colors.danger}>` */
export function Text({ variant = 'body', color, weight, align, size, style, ...rest }: AppTextProps) {
  const { colors } = useTheme();
  return (
    <RNText
      {...rest}
      style={[
        VARIANTS[variant](colors),
        color ? { color } : null,
        weight ? { fontWeight: font.weight[weight] } : null,
        align ? { textAlign: align } : null,
        size ? { fontSize: size } : null,
        style,
      ]}
    />
  );
}
